import { images } from '../../images';
import {
  IClient,
  IService,
  ITown,
  IUser,
  IUserRegular,
} from '../models';
import {
  CommunicationUserType,
  DriverSuggestionType,
  IncidenceAffair,
  InputType,
  ObjectFoundAffair,
  ObjectLostAffair,
  ObservationAffair,
  UserRole,
  UserSortType,
  UserStatus,
  UserType,
} from '../models/enums';
import { SuggestionPriority } from '../models/enums/suggestionPriority';
import { SuggestionStatus } from '../models/enums/suggestionStatus';
import { SuggestionType } from '../models/enums/suggestionType';
import { ISuggestion } from '../models/suggestion';
import {
  ClientsService,
  StorageService,
  UsersService,
} from '../services';
import { LiteralService } from '../services/literal/literal.service';
import { ServiceInfoService } from '../services/service-info/service-info.service';
import { SuggestionAffair } from '../models/enums/suggestionAffair';
import { ComplaintAffair } from '../models/enums/complaintAffair';
import { TooltipPosition } from '../shared/tooltip/tooltip-position';
import { formData } from './suggestionData';

export class UserSuggestionData {
  showMoreUsers: boolean;
  search?: boolean;
  value?: string;
  clientChanged?: boolean;

  constructor(data: Partial<UserSuggestionData>) {
    Object.assign(this, data);
  }
}

export async function getClients(usersService: UsersService) {
  return await usersService.getPossibleClientTowns().then((res) => {
    clients = [];
    towns = [];
    res.clients.map((client: IClient) => {
      clients.push({
        id: client.id,
        value: client,
        label: client.name,
        name: client.name,
      });
    });
  });
}

export async function getTownsByClient(usersService: UsersService, clientId: any, selected?: any[]) {
  return await usersService.getTownsByClient(clientId).then((res: any) => {
    towns = [];
    res.map((town: ITown) => {
      towns.push({ id: town.id, value: town, label: town.name });
    });
  });
}

export async function getServices(
  clientService: ClientsService,
  clientId: number,
) {
  return await clientService.getServices(clientId).then((res) => {
    const serviceInfos: any[] = [];
    res.map((service: IService) => {
      serviceInfos.push({
        id: service.id,
        value: service,
        label: service.name ? `${service.tag}: ${service.name}` : service.tag,
      });
    });

    return { serviceInfos: serviceInfos };
  });
}

export async function getServicesByTown(
  serviceInfoService: ServiceInfoService,
  townId: number,
) {
  return await serviceInfoService.getServiceInfosSearch(100, 1, [{ id: townId } as ITown]).then((res: any) => {
    const content = res.content || res;
    serviceInfos = [];
    (content as any[]).map((service: IService) => {
      serviceInfos.push({
        id: service.id,
        value: service,
        label: service.name ? `${service.tag}: ${service.name}` : service.tag,
      });
    });
  });
}

let clients: any[] = [];
let towns: any[] = [];
let serviceInfos: any[] = [];
let serviceInfosTownId: number | undefined;
let users: any[] = [];

export async function getSuggestionForm(
  suggestion: ISuggestion = {} as ISuggestion,
  userType: CommunicationUserType,
  literalService: LiteralService,
  usersService: UsersService,
  storageService: StorageService,
  clientsService: ClientsService,
  userData?: UserSuggestionData,
  changes?: any,
  editing?: boolean,
  creating?: boolean,
  currentStep?: number,
  serviceInfoService?: ServiceInfoService
): Promise<any> {
  const adminRole = await storageService.getRole();

  let statuses: any[] = [];
  let affairs: any[] = [];
  let suggestionAffairs: any[] = [];
  let complaintAffairs: any[] = [];
  let observationAffairs: any[] = [];
  let incidenceAffairs: any[] = [];
  let objectLostAffairs: any[] = [];
  let objectFoundAffairs: any[] = [];
  let types: any[] = [];
  let priorities: any[] = [];

  if (clients == undefined || clients.length == 0 ) {
    await getClients(usersService).then(() => {
      if (clients.length === 1) suggestion.client = clients[0].value;
    });
  }

  if (clients.length > 1 && currentStep === 1) towns = [];
  else if (suggestion.client && suggestion.client.id && (towns == undefined || towns.length == 0)) {
    await getTownsByClient(usersService, suggestion.client.id).then(() => {
      if (towns.length === 1) suggestion.town = towns[0].value;
    });
  }

  if (userData) {
    if (userData.showMoreUsers) {
      page += 1;
      pageSearched += 1;
    } else {
      users = [];
      page = 0;
      usersSearched = [];
      pageSearched = 0;
    }
    if (userData.search) {
      await searchUsers(
        usersService,
        userType === CommunicationUserType.DRIVERS ? UserType.Driver : UserType.Regular,
        suggestion.client?.id,
        suggestion.userSuggester,
        userData.value,
      );
    } else {
      await getUsers(
        usersService,
        userType === CommunicationUserType.DRIVERS ? UserType.Driver : UserType.Regular,
        suggestion.client?.id,
        suggestion.userSuggester,
      );
    }
  } else if (suggestion.userSuggester) {
    suggestion.userSuggester.id && addSelectedUser(users, suggestion.userSuggester as IUserRegular);
  }

  if (
    suggestion.town &&
    suggestion.town.id &&
    serviceInfoService &&
    (serviceInfos == undefined || serviceInfos.length == 0 || serviceInfosTownId !== suggestion.town.id)
  ) {
    serviceInfosTownId = suggestion.town.id;
    await getServicesByTown(serviceInfoService, suggestion.town.id);
  }
  Object.values(SuggestionStatus).forEach((status: SuggestionStatus) => {
    statuses.push({
      id: status,
      value: status,
      label: `suggestions.statuses.${status.toLowerCase()}`,
    });
  });
  Object.values(SuggestionPriority).forEach((priority: SuggestionPriority) => {
    priorities.push({
      id: priority,
      value: priority,
      label: `suggestions.priorities.${priority.toLowerCase()}`,
    });
  });
  if (userType === CommunicationUserType.USERS) {
    Object.values(SuggestionType).forEach((type: SuggestionType) => {
      types.push({
        id: type,
        value: type,
        label: `suggestions.${type.toLowerCase()}`,
      });
    });
    Object.values(SuggestionAffair).forEach((suggestionAffair: SuggestionAffair) => {
      suggestionAffairs.push({
        id: suggestionAffair,
        value: suggestionAffair,
        label: `suggestions.affairs.${suggestionAffair}`,
      });
    });
    Object.values(ComplaintAffair).forEach((complaintAffair: ComplaintAffair) => {
      complaintAffairs.push({
        id: complaintAffair,
        value: complaintAffair,
        label: `suggestions.affairs.${complaintAffair}`,
      });
    });
    Object.values(ObjectLostAffair).forEach((objectLostAffair: ObjectLostAffair) => {
      objectLostAffairs.push({
        id: objectLostAffair,
        value: objectLostAffair,
        label: `suggestions.affairs.${objectLostAffair}`,
      });
    });
  } else {
    Object.values(DriverSuggestionType).forEach((type: DriverSuggestionType) => {
      types.push({
        id: type,
        value: type,
        label: `suggestions.${type.toLowerCase()}`,
      });
    });
    Object.values(ObservationAffair).forEach((observationAffair: ObservationAffair) => {
      observationAffairs.push({
        id: observationAffair,
        value: observationAffair,
        label: `suggestions.affairs.${observationAffair}`,
      });
    });
    Object.values(IncidenceAffair).forEach((incidencesAffair: IncidenceAffair) => {
      incidenceAffairs.push({
        id: incidencesAffair,
        value: incidencesAffair,
        label: `suggestions.affairs.${incidencesAffair}`,
      });
    });
    Object.values(ObjectFoundAffair).forEach((objectFoundAffair: ObjectFoundAffair) => {
      objectFoundAffairs.push({
        id: objectFoundAffair,
        value: objectFoundAffair,
        label: `suggestions.affairs.${objectFoundAffair}`,
      });
    });
  }


  switch(suggestion.type) {
    case SuggestionType.COMPLAINT:
      affairs = complaintAffairs;
      break;
    case SuggestionType.OBJECT_LOST:
      affairs = objectLostAffairs;
      break;
    case DriverSuggestionType.OBSERVATION:
      affairs = observationAffairs;
      break;
    case DriverSuggestionType.INCIDENCE:
      affairs = incidenceAffairs;
      break;
    case DriverSuggestionType.OBJECT_FOUND:
      affairs = objectFoundAffairs;
      break;
    case SuggestionType.SUGGESTION:
    default:
      affairs = suggestionAffairs;
      break;
  }
  const isObjectLostType = suggestion.type === SuggestionType.OBJECT_LOST;
  const isObjectFoundType = suggestion.type === DriverSuggestionType.OBJECT_FOUND;
  if (isObjectLostType) {
    suggestion.affair = ObjectLostAffair.OBJECT_LOST;
  } else if (isObjectFoundType) {
    suggestion.affair = ObjectFoundAffair.OBJECT_FOUND;
  } else if (creating && suggestion.affair && !affairs.some((affair) => affair.value === suggestion.affair)) {
    suggestion.affair = affairs[0].value;
  }
  
  const hideClientsStep = clients.length === 1;
  const lastStep = hideClientsStep ? 2 : 3;

  const data: formData[] = [
    {
      id: 'client' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.clients,
      title: 'suggestions.data.client',
      value: creating && currentStep! === 1 ? suggestion.client : suggestion.client?.name,
      translateValue: true,
      color: true,
      step: 1,
      expanded: false,
      hidden:
        !creating ||
        hideClientsStep,
      edit: {
        editable: creating && currentStep! === 1 ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: clients,
        valueToChange: 'client',
      }
    },
    {
      id: 'area' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.area,
      title: 'suggestions.data.town',
      value: creating && currentStep! === (lastStep - 1) ? suggestion.town : suggestion.town?.name,
      translateValue: true,
      array: false,
      step: lastStep - 1,
      color: true,
      expanded: false,
      hidden: !creating || currentStep! < (lastStep - 1),
      edit: {
        editable: creating && currentStep! === (lastStep - 1) ? true : false,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: towns.length === 0,
        values: towns,
        valueToChange: 'town',
      }
    },
    {
      id: 'service' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.services,
      title: 'suggestions.data.service',
      value: creating && currentStep! === (lastStep - 1) ? suggestion.service : suggestion.service ? (suggestion.service.name ? `${suggestion.service.tag}: ${suggestion.service.name}` : suggestion.service.tag) : '',
      translateValue: true,
      array: false,
      step: lastStep - 1,
      color: true,
      expanded: false,
      hidden: !creating || currentStep! < (lastStep - 1),
      edit: {
        editable: creating && currentStep! === (lastStep - 1) ? true : false,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        disabled: serviceInfos.length === 0,
        values: serviceInfos,
        valueToChange: 'service',
      }
    },
    {
      id: 'user' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.user,
      title: 'suggestions.data.user',
      value: creating && currentStep === (lastStep - 1) ? 
        suggestion.userSuggester : 
        suggestion.userSuggester ? 
          `${suggestion.userSuggester?.firstName} ${suggestion.userSuggester?.lastName}` : 
          'suggestions.anonymousUser',
      translateValue: true,
      color: true,
      step: lastStep - 1,
      expanded: false,
      hidden: creating && currentStep! < (lastStep - 1),
      edit: {
        editable: creating && currentStep! === (lastStep - 1) ? true : false,
        required: true,
        inputType: InputType.INFINITE_SCROLL,
        values: userData && userData.search ? usersSearched : users,
        valueToChange: 'user',
        disabled: suggestion.userSuggester?.firstName === literalService.get('suggestions.anonymousUser', true)
      }
    },
    {
      id: 'type' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.complaint,
      title: 'suggestions.data.type',
      value: creating && currentStep === lastStep ? suggestion.type : `suggestions.${suggestion.type.toLowerCase()}`,
      translateValue: true,
      array: false,
      hidden: !creating || currentStep! !== lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: creating && currentStep! === lastStep ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: types,
        valueToChange: 'type',
      }
    },
    {
      id: 'affair' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.affair,
      title: 'suggestions.data.affair',
      value: creating && currentStep === lastStep && !isObjectLostType && !isObjectFoundType ? suggestion.affair : `suggestions.affairs.${suggestion.affair}`,
      array: false,
      translateValue: true,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: creating && currentStep! === lastStep && !isObjectLostType && !isObjectFoundType ? true : false,
        required: true,
        inputType: InputType.SELECT_SEARCH,
        values: affairs,
        valueToChange: 'affair',
      }
    },

    {
      id: 'description' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.comment,
      title: 'suggestions.comment',
      value: suggestion.description,
      array: false,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      placeholder: 'maxChar250',
      tooltipPosition: TooltipPosition.LEFT,
      expanded: false,
      edit: {
        editable: creating && currentStep! === lastStep ? true : false,
        inputType: InputType.LONG_TEXT,
        required: false,
        disabled: false,
        valueToChange: 'description',
        maxLength: 250,
        placeholder: literalService.get('maxChar250', true),
        rows: 4
      }
    },
    {
      id: 'state' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.status.pending,
      title: 'suggestions.data.status',
      value: ((creating && currentStep === lastStep) || editing) ? suggestion.status : `suggestions.statuses.${suggestion.status!.toLowerCase()}`,
      translateValue: true,
      step: lastStep,
      hidden: creating && currentStep! < lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: true,
        id: suggestion.status,
        inputType: InputType.SELECT_SEARCH,
        translateValue: false,
        required: false,
        disabled: false,
        values: statuses,
        valueToChange: 'status',
      },
    },
    {
      id: 'priority' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.arrowUp,
      title: 'suggestions.data.priority',
      value: ((creating && currentStep === lastStep) || editing) ? suggestion.priority : `suggestions.priorities.${suggestion.priority!.toLowerCase()}`,
      translateValue: true,
      array: false,
      hidden: creating && currentStep! < lastStep,
      step: lastStep,
      color: true,
      expanded: false,
      edit: {
        editable: true,
        id: suggestion.priority
          ? suggestion.priority
          : SuggestionPriority.MEDIUM,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: false,
        values: priorities,
        valueToChange: 'priority',
      },
    },
    {
      id: 'internNotes' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.internNotes,
      title: 'suggestions.data.internNotes',
      value: suggestion.comments ?? '',
      array: false,
      step: lastStep,
      color: true,
      hidden: editing && !suggestion.id || creating,
      tooltipPosition: TooltipPosition.LEFT,
      expanded: false,
      edit: {
        editable: true,
        inputType: InputType.LONG_TEXT,
        required: false,
        disabled: false,
        valueToChange: 'comments',
        maxLength: 250,
        placeholder: literalService.get('maxChar250', true),
        rows: 4,
      },
    },
    {
      id: 'serviceView' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.services,
      title: 'suggestions.data.service',
      value: suggestion.service ? (suggestion.service.name ? `${suggestion.service.tag}: ${suggestion.service.name}` : suggestion.service.tag) : '',
      translateValue: true,
      array: false,
      color: true,
      expanded: true,
      hidden: creating,
      edit: {
        editable: false,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: serviceInfos,
        valueToChange: 'service',
      }
    },
    {
      id: 'area' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.area,
      title: 'suggestions.data.town',
      value: suggestion.town?.name,
      translateValue: true,
      array: false,
      color: true,
      expanded: true,
      hidden: creating,
      edit: {
        editable: false,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        disabled: towns.length === 0,
        values: towns,
        valueToChange: 'town',
      }
    },
    {
      id: 'clients' + (suggestion.id ? `-${suggestion.id}` : ''),
      img: images.suggestions.clients,
      title: 'suggestions.data.client',
      value: suggestion.client?.name,
      dataToShow: 'code',
      color: true,
      expanded: true,
      hidden: clients.length <= 1 || creating,
      edit: {
        editable: false,
        inputType: InputType.MULTISELECT,
        required: false,
        valueToChange: 'clients'
      },
    },
  ];
  return data;
}

let page = 0;
let usersSearched: any[] = [];
let pageSearched = 0;

export async function getUsers(
  usersService: UsersService,
  userType: UserType,
  clientId?: number,
  userSelected?: IUser,
  searchValue?: string,
) {
  await usersService
    .filterByUserType(
      userType,
      UserSortType.ALPHABETICALLY,
      page,
      10,
      clientId ? [clientId] : [],
      [],
      [],
      [],
      searchValue,
    )
    .then((res) => {
      res.content.map((user: IUserRegular) => {
        if (
          user.status?.toUpperCase() === UserStatus.ACTIVE ||
          user.status?.toUpperCase() === UserStatus.BLOCKED
        ) {
          const extraInfo =
            user &&
            ((user.dni && user.dni) ||
              (user.phone && user.phone) ||
              (user.email && user.email));
          users.push({
            id: user.id,
            value: user,
            label:
              user.firstName === null && user.lastName === null
                ? `${user.email}`
                : `${user.firstName} ${user.lastName} (${extraInfo})`,
          });
        }
      });
    });

  userSelected && userSelected.id && addSelectedUser(users, userSelected as IUserRegular);
}

export async function searchUsers(
  usersService: UsersService,
  userType: UserType,
  clientId?: number,
  userSelected?: IUser,
  searchValue?: string,
) {
  await usersService
    .filterByUserType(
      userType,
      UserSortType.ALPHABETICALLY,
      pageSearched,
      10,
      clientId ? [clientId] : [],
      [],
      [],
      [],
      searchValue,
    )
    .then((res) => {
      res.content.map((user: IUserRegular) => {
        if (
          user.status?.toUpperCase() === UserStatus.ACTIVE ||
          user.status?.toUpperCase() === UserStatus.BLOCKED
        ) {
          const extraInfo =
            user &&
            ((user.dni && user.dni) ||
              (user.phone && user.phone) ||
              (user.email && user.email));
          usersSearched.push({
            id: user.id,
            value: user,
            label:
              user.firstName === null && user.lastName === null
                ? `${user.email}`
                : `${user.firstName} ${user.lastName} (${extraInfo})`,
          });
        }
      });
    });

  userSelected && userSelected.id && addSelectedUser(usersSearched, userSelected as IUserRegular);
}

export function addSelectedUser(userList: any, userSelected: IUserRegular) {
  const exists = userSelected && userList.find((user: any) => user.id === userSelected.id);
  if (!exists) {
    const extraInfo =
      userSelected &&
      ((userSelected.dni && userSelected.dni) ||
        (userSelected.phone && userSelected.phone) ||
        (userSelected.email && userSelected.email)) ||
        null;
    userSelected &&
    userList.unshift({
      id: userSelected.id,
      value: userSelected,
      label:
        userSelected.firstName === null && userSelected.lastName === null
          ? `${userSelected.email}`
          : `${userSelected.firstName} ${userSelected.lastName} ${extraInfo ? `(${extraInfo})` : '' }`,
    });
  }
}
